import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { graphql } from '~/gql'
import { useCurrentSurveyGroupIdState } from '~/hooks/useCurrentSurveyGroupIdState'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

import { useBrandAccountId } from '../useBrandAccountId'

import { useMainQueryKey } from './useMainQueryKey'

const document = graphql(`
  query TimeSeriesAnalysisPage_Main($brandAccountId: ID!) {
    brandAccount(brandAccountId: $brandAccountId) {
      id
      surveyGroups {
        id
        title
        surveys {
          id
        }
        brands {
          id
          name
        }
        questions(showCustomizedKPI: true) {
          id
          name
          answerOptions {
            option
            text
          }
        }
        customerAttributeGroups {
          id
          name
          showCustomizedKPI
          attachedCustomerAttributeGroups {
            surveyId
            customerAttributeGroupId
          }
          customerAttributes {
            id
            name
            attachedCustomerAttributes {
              surveyId
              customerAttributeId
            }
          }
        }
      }
      surveys {
        id
        title
        numberOfTotalAnswers
        metadata {
          surveyStartDate {
            year
            month
            day
          }
          tamNumber
        }
        customerAttributeGroups {
          id
          customerAttributes {
            id
            name
          }
        }
      }
    }
  }
`)

export const useMainQuery = () => {
  const graphQLRequest = useGraphQLRequest()
  const brandAccountId = useBrandAccountId()
  const queryKey = useMainQueryKey()
  const [surveyGroupId, setSurveyGroupId] = useCurrentSurveyGroupIdState()

  const result = useQuery({
    queryFn: () => graphQLRequest({ document, variables: { brandAccountId } }),
    queryKey,
  })
  const { data, isSuccess } = result

  React.useEffect(() => {
    if (isSuccess) {
      const surveyGroups = data.brandAccount?.surveyGroups ?? []

      if (
        exists(surveyGroupId) &&
        surveyGroups.some((surveyGroup) => surveyGroup.id === surveyGroupId)
      ) {
        return
      }

      const surveyGroup = surveyGroups[0]
      if (exists(surveyGroup)) {
        setSurveyGroupId(surveyGroup.id)
      }
    }
  }, [
    data?.brandAccount?.surveyGroups,
    isSuccess,
    setSurveyGroupId,
    surveyGroupId,
  ])

  return result
}
